import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FirebaseAppContext } from 'libs.firebase_react';
import { AssetTypes, LocalizationStatus } from 'libs.localization.common';
import { useLocalization } from 'libs.localization.react';
import { useLocalizeMessage, Workflow, useToastNotification } from 'libs.nucleus';
import { ButtonWithTooltip, useBooleanState } from 'libs.react';
import { LocaleStatusChangeModal } from './locale_status_change_modal.component';
import { useLocalizedStatusChange } from '../../hooks';
import { useLocalizedBundle } from '../../queries/use_localized_bundle';
export const LocaleStatusProgress = ({ asset, locale, assetId, isDisabled = false, isCustomActivity = false, trackingContext = {}, onStatusChange, disabledMessage = '', }) => {
    const t = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const [isModalOpen, openModal, closeModal] = useBooleanState(false);
    const { logEvent } = useContext(FirebaseAppContext);
    const [isChanging, setIsChanging] = useState(false);
    const { localizationEntry, localization, isLoading, isScreenReportReady } = useLocalization(asset);
    const status = localizationEntry?.supported_locales?.[locale]?.status;
    const version = localizationEntry?.supported_locales?.[locale]?.version;
    const _version = useRef(version);
    const enableLocalizedBundle = asset && ('activity' in asset || asset.type === AssetTypes.ACTIVITY);
    const { isPending, mutate: pushTranslationBundle } = useLocalizedBundle(isCustomActivity);
    const { mutate: setLocalizationStatus } = useLocalizedStatusChange();
    // Use refs for values that are only used for logging/tracking to avoid unnecessary rerenders
    // trackingContext is often passed as inline object literal, causing handler recreation
    const trackingContextRef = useRef(trackingContext);
    const onStatusChangeRef = useRef(onStatusChange);
    useEffect(() => {
        trackingContextRef.current = trackingContext;
        onStatusChangeRef.current = onStatusChange;
    }, [trackingContext, onStatusChange]);
    const statusProps = useMemo(() => [
        {
            iconName: 'circle',
            color: 'redPale',
            label: t('Not Started'),
            value: LocalizationStatus.NOT_STARTED,
        },
        {
            iconName: 'circle',
            color: 'orangePale',
            label: t('In Progress'),
            value: LocalizationStatus.IN_PROGRESS,
        },
        {
            iconName: 'circle',
            color: 'yellowPale',
            label: t('In Review'),
            value: LocalizationStatus.IN_REVIEW,
        },
        {
            iconName: 'circle',
            color: 'greenPale',
            label: t('Ready for use'),
            value: LocalizationStatus.READY,
        },
    ], []);
    useEffect(() => {
        if (localizationEntry) {
            _version.current = localizationEntry.supported_locales?.[locale].version;
        }
    }, [localizationEntry]);
    const onHandleStatus = useCallback(async (selectedStatus) => {
        setIsChanging(true);
        try {
            if (!localization) {
                addNotification({
                    title: t('Internal error. Localization instance is missing.'),
                    type: 'error',
                });
                setIsChanging(false);
                return;
            }
            if (!version) {
                addNotification({
                    title: t('Internal error. Version is missing.'),
                    type: 'error',
                });
                setIsChanging(false);
                return;
            }
            if (assetId) {
                logEvent('translations_localization_status_changed', {
                    ...trackingContextRef.current,
                    previousStatus: status,
                    status: selectedStatus,
                    locale,
                    id: assetId,
                });
            }
            // Status: Ready for use
            if (selectedStatus === LocalizationStatus.READY && assetId) {
                // Activities have a special route (instrument bundler)
                if (enableLocalizedBundle) {
                    pushTranslationBundle({ resourceId: assetId, locales: { [locale]: Number(version) } }, {
                        onSuccess: () => {
                            closeModal();
                            if (onStatusChangeRef.current) {
                                onStatusChangeRef.current({
                                    status: selectedStatus,
                                    id: localization.getId(),
                                    version: _version.current ?? version,
                                    locale,
                                });
                            }
                            setIsChanging(false);
                        },
                        onError: (error) => {
                            console.error('Failed to push translation bundle:', error);
                            addNotification({
                                title: t('Failed to update translation status. Please try again.'),
                                type: 'error',
                            });
                            setIsChanging(false);
                        },
                    });
                    return;
                }
                // All other assets go here.
                setLocalizationStatus({ asset, locale, status: selectedStatus }, {
                    onSuccess: () => {
                        closeModal();
                        if (onStatusChange) {
                            onStatusChange({
                                status: selectedStatus,
                                id: localization.getId(),
                                version: _version.current ?? version,
                                locale,
                            });
                        }
                        setIsChanging(false);
                    },
                    onError: (error) => {
                        console.error('Failed to set localization status:', error);
                        addNotification({
                            title: t('Failed to update translation status. Please try again.'),
                            type: 'error',
                        });
                        setIsChanging(false);
                    },
                });
                return;
            }
            // first set the status in firebase
            await localization?.setStatus(locale, selectedStatus);
            if (onStatusChangeRef.current) {
                onStatusChangeRef.current({
                    status: selectedStatus,
                    id: localization.getId(),
                    version: _version.current ?? version,
                    locale,
                });
            }
            setIsChanging(false);
            closeModal();
        }
        catch (error) {
            console.error('Error setting localization status:', error);
            addNotification({
                title: t('Failed to update translation status. Please try again.'),
                type: 'error',
            });
            setIsChanging(false);
        }
    }, [
        status,
        version,
        localization,
        asset,
        locale,
        assetId,
        enableLocalizedBundle,
        pushTranslationBundle,
        setLocalizationStatus,
        addNotification,
        t,
        logEvent,
        closeModal,
    ]);
    const currentStatusIndex = statusProps.findIndex((statusProp) => statusProp.value === status);
    const screenReportReady = isScreenReportReady(locale, undefined, _version.current ?? version);
    const isChangeStatusDisabled = isDisabled || isLoading || (enableLocalizedBundle && !screenReportReady);
    return (_jsxs("div", { className: 'flex gap-4 items-center relative', "data-testid": 'translation-status-workflow', children: [_jsx("div", { className: 'flex', children: _jsx(Workflow, { statuses: statusProps, activeStatusIndex: currentStatusIndex < 0 ? 0 : currentStatusIndex }) }), _jsx(ButtonWithTooltip, { label: 'Change status', onClick: openModal, disabled: isChangeStatusDisabled, tooltip: isDisabled ? disabledMessage : '', enableTooltip: isDisabled, tooltipPlacement: 'bottom', variant: 'secondary' }), isModalOpen && status && (_jsx(LocaleStatusChangeModal, { isChanging: isPending || isChanging, onModalClose: closeModal, onHandleStatus: onHandleStatus, status: status }))] }));
};
